// M+3.7.2 Stage 3 #2 — modal label/title translation maps. Maps the English
// literals used throughout App.jsx's modal helpers (QF/QS/QT/F/S/T) to
// translation keys. The helpers fall back to the English literal when no
// key exists. Keys live under field.* / modal.title.* / modal.helper.* in
// the Translation table (seeded by seed_deploy_m372_translations.js).
// Extracted verbatim from src/App.tsx (B.6, extract-don't-improve).
export const _LBL_KEYS: Record<string, any> = {
  // Personal name / contact
  "First Name *": "field.first_name_required",
  "First Name": "field.first_name",
  "Last Name *": "field.last_name_required",
  "Last Name": "field.last_name",
  Title: "field.title",
  Email: "field.email",
  Phone: "field.phone",
  // 59 §2 / 45a — "Tel" is the jurisdiction form's own abbreviation and is a
  // SEPARATE label from "Phone", not a synonym for it: both appear, on
  // different forms, and one key for two words would make the abbreviation
  // impossible to keep short in French.
  Tel: "field.tel",
  Initials: "field.initials",
  // 164 §C.2 — the "Initials note" entry is gone: its one reader was a
  // <label> that resolved to the very paragraph rendered beneath it, so the
  // note printed twice. The paragraph reads modal.co.initials_note directly.
  // 164 §C.3 — the admin checkbox's label had no entry, so
  // makeLabelTranslators returned the English literal on every language.
  Administrator: "modal.co.administrator",
  "Function / Role": "field.function_role",
  // Address
  Country: "field.country",
  "Postal Code": "field.postal_code",
  City: "field.city",
  Street: "field.street",
  // Identifiers
  VAT: "field.vat",
  "National Register ID": "field.national_register_id",
  "National Register Number": "field.national_register_number",
  "Professional Registration": "field.professional_registration",
  Bar: "field.bar",
  "Bar / Professional Reg.": "field.bar_pro_reg",
  // Org / role
  Type: "field.type",
  Company: "field.company",
  "Company Name *": "field.company_name_required",
  Firm: "field.firm",
  Specialty: "field.specialty",
  Office: "field.office",
  "Office / Practice": "field.office_practice",
  Practice: "field.practice",
  Chamber: "field.chamber",
  Organisation: "field.organisation",
  // Misc
  Notes: "field.notes",
  Note: "field.note",
  Contact: "field.contact",
  "Contact (additional)": "field.contact_additional",
  "Linked to Client *": "field.linked_to_client_required",
  "Linked to Opponent *": "field.linked_to_opponent_required",
  "Linked Client": "field.linked_client",
  "Linked Opponent": "field.linked_opponent",
  Quantum: "field.quantum",
  // 182 B.2 — the prospect columns on the client modal.
  Source: "field.lead_source",
  "Referred by": "field.referred_by",
  "Lost reason": "field.lost_reason",
  "Former Case Ref": "field.former_case_ref",
  // 211 §16 [RULED 24] — the client's own « Ref. contract » (Client.contractNumber); a case's numbers keep
  // `field.contractNumber` (the members table, the add window)
  "Ref. contract": "field.client_contract_ref",
  Language: "field.language",
  "Default Ratio": "field.default_ratio",
  Role: "field.role",
  Status: "field.status",
  "Type *": "field.type_required",
  "Name *": "field.name_required",
  "Case *": "field.case_required",
  Stage: "field.stage",
  Jurisdiction: "field.jurisdiction",
  "Start Date": "field.start_date",
  "End Date": "field.end_date",
  Date: "field.date",
  "Link *": "field.link_required",
  "File Type": "field.file_type",
  "Register Number": "field.register_number",
  "Ref *": "field.ref_required",
  "Title *": "field.title_required",
  "Open Date": "field.open_date",
  // 55 §3 — the KEY keeps its name (a key is an identity and renaming it
  // orphans three seeded rows); the English it falls back to is the word
  // Laurent ruled, and it equals the seeded `en` row.
  "Suspended Since": "field.pending_since",
  "Closed On": "field.closed_on",
  "Archived On": "field.archived_on",
  Description: "field.description",
  "Responsible Partner": "field.responsible_partner",
  "SharePoint / OneDrive Folder": "field.sharepoint_folder",
  Reminder: "field.reminder",
  // Phase 2a — Stage 3 correction: jurisdiction modal + cross-modal hardcoded
  // labels that bypassed _fl until C1.
  "Court *": "field.court_required",
  Address: "field.address",
  "Civil/Burg.": "field.greffe_civil",
  "Crim./Corr.": "field.greffe_penal",
  "Fam.": "field.greffe_famille",
  "Bar Association": "field.bar_association",
  "Activity Type": "field.activity_type",
  "Due Date": "field.due_date",
  Priority: "field.priority",
  "Assign to *": "field.assign_to_required",
};
export const _MT_KEYS: Record<string, any> = {
  "New Client": "modal.title.new_client",
  "Edit Client": "modal.title.edit_client",
  "New Opponent": "modal.title.new_opponent",
  "Edit Opponent": "modal.title.edit_opponent",
  "New Opp. Counsel": "modal.title.new_opp_counsel",
  "Edit Opp. Counsel": "modal.title.edit_opp_counsel",
  "New Expert": "modal.title.new_expert",
  "Edit Expert": "modal.title.edit_expert",
  "New Mediator": "modal.title.new_mediator",
  "Edit Mediator": "modal.title.edit_mediator",
  "New Arbiter": "modal.title.new_arbiter",
  "Edit Arbiter": "modal.title.edit_arbiter",
  "New Other Person": "modal.title.new_other_person",
  "New Person": "modal.title.new_person",
  "Edit Person": "modal.title.edit_person",
  "New Contact Person": "modal.title.new_contact_person",
  "New Contact": "modal.title.new_contact",
  "Edit Contact": "modal.title.edit_contact",
  "New Opp. Contact": "modal.title.new_opp_contact",
  "Edit Opp. Contact": "modal.title.edit_opp_contact",
  "New Jurisdiction": "modal.title.new_jurisdiction",
  "Edit Jurisdiction": "modal.title.edit_jurisdiction",
  "New Collaborator": "modal.title.new_collaborator",
  "Edit Collaborator": "modal.title.edit_collaborator",
  "New Case": "modal.title.new_case",
  "Edit Case": "modal.title.edit_case",
  "New Task": "modal.title.new_task",
  "Edit Task": "modal.title.edit_task",
  "New Event": "modal.title.new_event",
  "Edit Event": "modal.title.edit_event",
  "Edit Time": "modal.title.edit_time",
  "Edit Doc": "modal.title.edit_doc",
  "Edit Stage": "modal.title.edit_stage",
};
